import React, { useEffect, useState } from 'react';
import {
  Box, Flex, Text, Grid, Badge, Spinner, Button, Divider,
  Modal, ModalOverlay, ModalContent, useToast,
} from '@chakra-ui/react';
import { FiExternalLink, FiCopy, FiDownload, FiArrowRight, FiRefreshCw } from 'react-icons/fi';
import dayjs from 'dayjs';
import {
  getRecordingLogDetail, retryRecording, RETRYABLE_STATUSES, STATUS_LABELS, STATUS_COLOR,
} from 'api/administrador/monitoring/zoomRecordings';

const RING_STYLE = {
  ok:      { bg: '#DCFCE7', color: '#15803D', border: '#10B981', icon: '✓' },
  err:     { bg: '#FEE2E2', color: '#B91C1C', border: '#EF4444', icon: '✕' },
  run:     { bg: '#DBEAFE', color: '#1D4ED8', border: '#3B82F6', icon: '↻' },
  pending: { bg: '#F1F5F9', color: '#94A3B8', border: '#E2E8F0', icon: '·' },
};

function Stage({ variant, title, meta, isLast }) {
  const style = RING_STYLE[variant];
  return (
    <Flex gap={3} position="relative" pb={isLast ? 0 : 3}>
      <Flex
        w="26px" h="26px" borderRadius="999px" align="center" justify="center"
        bg={style.bg} color={style.color} border="2px solid" borderColor={style.border}
        fontSize="xs" fontWeight="700" flexShrink={0} zIndex={1}
      >
        {style.icon}
      </Flex>
      {!isLast && (
        <Box
          position="absolute" left="12px" top="30px" bottom={0}
          w="2px"
          bg={variant === 'ok' ? '#10B981' : variant === 'err' ? '#EF4444' : '#E2E8F0'}
          zIndex={0}
        />
      )}
      <Box flex={1} pt="2px">
        <Text fontSize="sm" fontWeight="600" color={variant === 'pending' ? 'gray.500' : 'gray.900'}>
          {title}
        </Text>
        <Text fontSize="xs" color={variant === 'err' ? 'red.700' : 'gray.500'}>{meta}</Text>
      </Box>
    </Flex>
  );
}

function HeaderBadge({ children }) {
  return (
    <Badge
      bg="rgba(255,255,255,.24)" color="white"
      px={2.5} py={1} borderRadius="999px" fontSize="11px" fontWeight="600"
      display="inline-flex" alignItems="center" gap="6px" textTransform="none"
    >
      {children}
    </Badge>
  );
}

function KV({ k, v, mono }) {
  return (
    <Box>
      <Text
        fontSize="10px"
        color="gray.400"
        fontWeight="600"
        textTransform="uppercase"
        letterSpacing=".05em"
        mb="3px"
      >
        {k}
      </Text>
      <Text
        fontSize="sm"
        color="gray.900"
        fontWeight="500"
        fontFamily={mono ? 'mono' : 'body'}
        wordBreak="break-word"
        lineHeight={1.35}
      >
        {v ?? '—'}
      </Text>
    </Box>
  );
}

function buildStages(log) {
  const stages = [];
  stages.push({
    variant: 'ok',
    title: 'Webhook recibido',
    meta: `${dayjs(log.createdAt).format('HH:mm:ss')} · firma verificada`,
  });
  if (log.stage === 'filter') {
    stages.push({
      variant: log.status === 'skipped_short' ? 'err' : 'ok',
      title: log.status === 'skipped_short' ? 'Descartada por corta' : 'Filtro aplicado',
      meta: `duración ${log.duration_minutes || 0} min`,
    });
    return stages;
  }
  if (log.status === 'skipped_no_live_class') {
    stages.push({
      variant: 'pending',
      title: 'Sin clase asociada — no se procesa',
      meta: 'no hay live-class para este meeting; el video sigue en Zoom',
    });
    return stages;
  }
  if (log.stage === 'dedupe' || log.status === 'duplicate') {
    stages.push({ variant: 'ok', title: 'Duplicada — descartada', meta: 'hermano más largo encontrado' });
    return stages;
  }
  stages.push({
    variant: log.status === 'enqueued' ? 'run' : 'ok',
    title: 'Encolada al worker',
    meta: 'firma HMAC OK',
  });
  if (log.status === 'enqueued') {
    stages.push({ variant: 'pending', title: 'Descarga y upload', meta: 'pendiente' });
    return stages;
  }
  if (log.status === 'failed') {
    stages.push({
      variant: 'err',
      title: `Fallo en ${log.stage || 'worker'}`,
      meta: log.error_message ? String(log.error_message).slice(0, 120) : (log.error_code || 'error'),
    });
    return stages;
  }
  if (log.status === 'unmatched') {
    stages.push({ variant: 'ok', title: 'Descargada de Zoom', meta: log.file_size_bytes ? `${Math.round(log.file_size_bytes / 1024 / 1024)} MB` : '—' });
    stages.push({ variant: 'ok', title: 'Subida a S3 OK', meta: 'multipart upload' });
    stages.push({ variant: 'err', title: 'Sin live-class', meta: 'asociar manual' });
    return stages;
  }
  stages.push({ variant: 'ok', title: 'Descargada de Zoom', meta: log.file_size_bytes ? `${Math.round(log.file_size_bytes / 1024 / 1024)} MB` : '—' });
  stages.push({ variant: 'ok', title: 'Subida a S3', meta: 'multipart upload' });
  stages.push({ variant: 'ok', title: 'Thumbnail generado', meta: 'frame @ 15s' });
  stages.push({ variant: 'ok', title: 'Callback a klazia-api', meta: 'HMAC OK' });
  const sharedPrograms = Array.isArray(log.shared_programs) ? log.shared_programs : [];
  stages.push({
    variant: 'ok',
    title: sharedPrograms.length > 0
      ? `Recorded-class creada · compartida con ${sharedPrograms.length} programa(s) más`
      : 'Recorded-class creada',
    meta: log.recorded_class
      ? `#${log.recorded_class.id} · asociada a live-class #${log.live_class?.id || '?'}`
      : '—',
  });
  return stages;
}

/**
 * Modal de detalle. Layout alineado al artifact de diseño:
 *   1. Header con degradado sin espacio blanco arriba, close X sobre el degradado
 *   2. Video preview full-width justo debajo del header (solo en éxito)
 *   3. Datos (single column stacked: label arriba, valor abajo) + Proceso (timeline) en 2 columnas
 *   4. Payload snapshot dark — SOLO en modales de error/unmatched
 *   5. Footer: chips a la izquierda, 3 botones a la derecha
 */
export default function ZoomLogDetailModal({ isOpen, setIsOpen, logId, onRetried }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const toast = useToast();

  useEffect(() => {
    if (!isOpen || !logId) return;
    let cancelled = false;
    setLoading(true);
    getRecordingLogDetail(logId)
      .then((res) => { if (!cancelled) setDetail(res?.data || null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, logId]);

  const log = detail;
  const stages = log ? buildStages(log) : [];

  const isError = log && (log.status === 'failed' || log.status === 'unmatched');
  const isOk = log && log.status === 'recorded_class_created';
  const isPending = log && ['webhook_received', 'enqueued', 'download_started', 'upload_completed'].includes(log.status);
  const isFilter = log && ['skipped_short', 'skipped_no_file', 'skipped_no_live_class', 'duplicate'].includes(log.status);

  const headerGradient = isError
    ? 'linear(135deg, #B91C1C 0%, #DC2626 55%, #F97316 100%)'
    : isFilter
      ? 'linear(135deg, #64748B 0%, #94A3B8 100%)'
      : isPending
        ? 'linear(135deg, #1E3A8A 0%, #3B82F6 100%)'
        : 'linear(135deg, #6D48E5 0%, #8B5CF6 100%)';
  const headerIcon = isError ? '!' : isFilter ? '≠' : isPending ? '↻' : '✓';

  const videoUrl = log?.recorded_class?.video;
  const liveClassId = log?.live_class?.id;

  /**
   * URL en la plataforma del estudiante (klazia3.0). Estructura:
   *   /program/<program_type.slug>/grade/<grade.id>/<grade.slug>/recorded/<recorded_class.id>
   * Si el log no tiene todos los campos poblados (grade + program_type),
   * devolvemos null y el botón queda deshabilitado — evita llevar al admin
   * a un 404.
   */
  // El API arma la URL (grados y carreras técnicas); el cálculo local queda
  // como respaldo para grados si el API todavía no la envía.
  const studentUrl = log?.student_url || (() => {
    const rc = log?.recorded_class;
    const grade = log?.live_class?.grade;
    const programType = grade?.program_type;
    if (!rc?.id || !grade?.id || !grade?.slug || !programType?.slug) return null;
    return `https://klazia.com/program/${programType.slug}/grade/${grade.id}/${encodeURIComponent(grade.slug)}/recorded/${rc.id}`;
  })();

  const onClose = () => setIsOpen(false);

  const handleRetry = async () => {
    if (!log || retrying) return;
    setRetrying(true);
    try {
      await retryRecording(log.id);
      toast({ title: 'Reintento encolado', description: 'En unos minutos verás el nuevo estado.', status: 'success', duration: 4000, isClosable: true });
      if (onRetried) onRetried();
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'No se pudo reintentar';
      toast({ title: 'No se pudo reintentar', description: msg, status: 'error', duration: 7000, isClosable: true });
    } finally {
      setRetrying(false);
    }
  };

  const handleCopyUrl = async () => {
    if (!videoUrl) return;
    try {
      await navigator.clipboard.writeText(videoUrl);
      toast({ title: 'URL copiada', status: 'success', duration: 2000, isClosable: true });
    } catch (e) {
      toast({ title: 'No se pudo copiar', status: 'error', duration: 2000 });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="outside">
      <ModalOverlay />
      <ModalContent borderRadius="18px" overflow="hidden" my={8}>
        {loading || !log ? (
          <Flex justify="center" py={20}><Spinner /></Flex>
        ) : (
          <Box>
            {/* HEADER con degradado — sin margen top, close X sobre el degradado */}
            <Box bgGradient={headerGradient} px={6} py={5} color="white" position="relative">
              <Flex
                position="absolute"
                top="16px" right="16px"
                w="30px" h="30px" borderRadius="8px"
                bg="rgba(255,255,255,.2)"
                align="center" justify="center"
                fontSize="16px" fontWeight="600"
                cursor="pointer"
                onClick={onClose}
                _hover={{ bg: 'rgba(255,255,255,.3)' }}
                transition="background .15s"
              >
                ×
              </Flex>
              <Flex align="flex-start" gap={4} pr="40px">
                <Flex
                  w="44px" h="44px" borderRadius="12px" bg="rgba(255,255,255,.18)"
                  align="center" justify="center" fontSize="20px" fontWeight="700" flexShrink={0}
                >
                  {headerIcon}
                </Flex>
                <Box flex={1}>
                  <Text fontSize="10px" textTransform="uppercase" letterSpacing=".08em" opacity={0.85} fontWeight="600">
                    Grabación · {log.start_time ? dayjs(log.start_time).format('DD MMM YYYY · HH:mm') : 'sin fecha'}
                  </Text>
                  <Text fontSize="xl" fontWeight="700" letterSpacing="-.01em" mt="2px">
                    {log.topic || 'Sin tema'}
                  </Text>
                  <Flex gap={2} mt={2} align="center" wrap="wrap">
                    <HeaderBadge>
                      <Box w="6px" h="6px" borderRadius="999px" bg={isError ? '#FCA5A5' : isPending ? '#93C5FD' : '#86EFAC'} />
                      {STATUS_LABELS[log.status] || log.status}
                    </HeaderBadge>
                    {log.live_class && <HeaderBadge>live_class #{log.live_class.id}</HeaderBadge>}
                    {log.recorded_class && <HeaderBadge>recorded #{log.recorded_class.id}</HeaderBadge>}
                  </Flex>
                </Box>
              </Flex>
            </Box>

            {/* BODY */}
            <Box p={6}>
              {/* VIDEO ARRIBA, FULL WIDTH — solo en éxito */}
              {isOk && videoUrl && (
                <Box mb={5} borderRadius="12px" overflow="hidden" bg="#0F172A">
                  <video
                    controls
                    poster={log.thumbnail_url}
                    style={{ width: '100%', maxHeight: 380, display: 'block' }}
                  >
                    <source src={videoUrl} type="video/mp4" />
                  </video>
                </Box>
              )}

              <Grid templateColumns={{ base: '1fr', md: '1fr 1.25fr' }} gap={{ base: 5, md: 8 }}>
                {/* DATOS — label arriba, valor abajo (single column dentro de la columna) */}
                <Box>
                  <Text fontSize="11px" fontWeight="700" letterSpacing=".05em" textTransform="uppercase" color="gray.500" mb={3}>
                    Datos
                  </Text>
                  <Grid templateColumns="1fr" gap={3}>
                    <KV k="Cuenta host" v={log.host_email} mono />
                    <KV k="Materia" v={log.live_class?.subject?.name} />
                    <KV k="live_class" v={log.live_class ? `#${log.live_class.id} · ${log.live_class.title || ''}` : '—'} />
                    <KV k="class_group" v={log.live_class?.class_group ? `#${log.live_class.class_group.id} · ${log.live_class.class_group.name || ''}` : '—'} />
                    <KV k="Meeting ID" v={log.meeting_id} mono />
                    <KV k="Meeting UUID" v={log.meeting_uuid} mono />
                    <KV k="Duración" v={log.duration_minutes ? `${log.duration_minutes} min` : '—'} />
                    <KV k="Tamaño" v={log.file_size_bytes ? `${Math.round(log.file_size_bytes / 1024 / 1024)} MB` : '—'} />
                    <KV k="Event ID" v={log.event_id} mono />
                    {log.error_code && <KV k="Error code" v={log.error_code} mono />}
                    {log.error_message && <KV k="Error msg" v={log.error_message} mono />}
                  </Grid>
                </Box>

                {/* PROCESO */}
                <Box>
                  <Text fontSize="11px" fontWeight="700" letterSpacing=".05em" textTransform="uppercase" color="gray.500" mb={3}>
                    Proceso
                  </Text>
                  <Box position="relative">
                    {stages.map((s, i) => (
                      <Stage key={i} {...s} isLast={i === stages.length - 1} />
                    ))}
                  </Box>

                  {/* Clase compartida: la misma grabación se ve desde estos programas
                       (por matrícula en la materia adicional). */}
                  {Array.isArray(log.shared_programs) && log.shared_programs.length > 0 && (
                    <>
                      <Text fontSize="11px" fontWeight="700" letterSpacing=".05em" textTransform="uppercase" color="gray.500" mt={5} mb={2}>
                        También visible en
                      </Text>
                      <Flex direction="column" gap={1.5}>
                        {log.shared_programs.map((p, i) => (
                          <Flex
                            key={`${p.program || ''}-${p.subject || ''}-${i}`}
                            align="center" gap={2} p={2}
                            borderRadius="8px" bg="gray.50"
                            fontSize="12px" color="gray.700"
                          >
                            <Box w="6px" h="6px" borderRadius="999px" bg="#10B981" flexShrink={0} />
                            <Box flex={1} minW={0}>
                              <Text isTruncated>{p.program || 'Programa'}</Text>
                              <Text isTruncated fontSize="11px" color="gray.500">
                                {p.subject}
                                {p.groups && p.groups.length > 0 ? ` · ${p.groups.join(', ')}` : ' · todos los grupos'}
                              </Text>
                            </Box>
                            {p.student_url && (
                              <Button as="a" href={p.student_url} target="_blank" rel="noopener noreferrer"
                                size="xs" variant="ghost" color="#6D48E5"
                                rightIcon={<FiExternalLink />} px={2}>
                                Abrir
                              </Button>
                            )}
                          </Flex>
                        ))}
                      </Flex>
                    </>
                  )}

                  {/* Payload snapshot: sólo en error/unmatched. En éxito no aporta y satura el modal. */}
                  {isError && log.payload_snapshot && (
                    <>
                      <Text fontSize="11px" fontWeight="700" letterSpacing=".05em" textTransform="uppercase" color="gray.500" mt={5} mb={2}>
                        Payload snapshot
                      </Text>
                      <Box bg="#0F172A" color="#E2E8F0" p={3} borderRadius="10px" fontSize="10px" fontFamily="mono" maxH="200px" overflow="auto">
                        <pre style={{ margin: 0 }}>{JSON.stringify(log.payload_snapshot, null, 2)}</pre>
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>

              {/* FOOTER: chips izq, 3 botones der */}
              <Divider my={5} />
              <Flex gap={3} justify="space-between" align="center" wrap="wrap">
                <Flex gap={2} wrap="wrap">
                  {isOk && (
                    <>
                      <Badge bg="#ECFDF5" color="#047857" px={2.5} py={1} borderRadius="6px" fontSize="11px" textTransform="none">
                        Visible en admin
                      </Badge>
                      <Badge bg="#ECFDF5" color="#047857" px={2.5} py={1} borderRadius="6px" fontSize="11px" textTransform="none">
                        Visible en klazia3.0
                      </Badge>
                    </>
                  )}
                  {log.status === 'failed' && (
                    <Badge bg="#FEF2F2" color="#B91C1C" px={2.5} py={1} borderRadius="6px" fontSize="11px" textTransform="none">
                      Estudiantes no lo ven
                    </Badge>
                  )}
                  {log.status === 'unmatched' && (
                    <Badge bg="#FFFBEB" color="#B45309" px={2.5} py={1} borderRadius="6px" fontSize="11px" textTransform="none">
                      Esperando asociación
                    </Badge>
                  )}
                </Flex>

                {isOk ? (
                  <Flex gap={2} wrap="wrap">
                    <Button leftIcon={<FiCopy />} variant="outline" size="sm" onClick={handleCopyUrl}>
                      Copiar URL video
                    </Button>
                    <Button
                      as="a" href={videoUrl} download
                      leftIcon={<FiDownload />} variant="outline" size="sm"
                    >
                      Descargar
                    </Button>
                    <Button
                      as="a"
                      href={studentUrl || '#'}
                      target="_blank" rel="noopener noreferrer"
                      rightIcon={<FiArrowRight />}
                      size="sm"
                      bg="#6D48E5" color="white" _hover={{ bg: '#5B34D1' }}
                      isDisabled={!studentUrl}
                      title={studentUrl ? 'Abrir en la plataforma del estudiante' : 'Faltan datos del programa (grado o carrera técnica) para armar la URL'}
                    >
                      Ver clase
                    </Button>
                  </Flex>
                ) : (
                  <Flex gap={2} wrap="wrap">
                    {RETRYABLE_STATUSES.includes(log.status) && (
                      <Button
                        leftIcon={<FiRefreshCw />} size="sm"
                        bg="#6D48E5" color="white" _hover={{ bg: '#5B34D1' }}
                        onClick={handleRetry} isLoading={retrying} loadingText="Reintentando"
                      >
                        Reintentar
                      </Button>
                    )}
                    {(log.status === 'unmatched' || log.status === 'failed') && log.meeting_id && (
                      <Button
                        as="a"
                        href={`https://zoom.us/recording/detail?meeting_id=${log.meeting_id}`}
                        target="_blank" rel="noopener noreferrer"
                        leftIcon={<FiExternalLink />} variant="outline" size="sm"
                      >
                        Ver en Zoom
                      </Button>
                    )}
                  </Flex>
                )}
              </Flex>
            </Box>
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
}
